import { ColumnState, TaskStyles } from '../../types';
import { RECRUITING_SLA_SCHEDULE, RECRUITING_SLA_WORKING_DAY, TaskDeadlineStyle } from '../../constants';
import moment, { MomentInput } from 'moment-timezone';

const CANCELLATION_CONFIRMATION_CONFIG: Record<string, { current: string[]; next: string; message: string }> = {
  ScreeningCommunication: {
    current: ['ScreeningCommunicationScheduled', 'ScreeningCommunicationCompleted'],
    next: 'ScreeningCommunicationPending',
    message: 'Do you really want to cancel the screening call with {candidateName}?',
  },
  GeneralInterview: {
    current: ['GeneralInterviewScheduled', 'GeneralInterviewCompleted'],
    next: 'GeneralInterviewPending',
    message: 'Do you really want to cancel the general interview with {candidateName}?',
  },
  RoleRelatedInterview: {
    current: ['RoleRelatedInterviewScheduled', 'RoleRelatedInterviewCompleted'],
    next: 'RoleRelatedInterviewPending',
    message: 'Do you really want to cancel the role-related interview with {candidateName}?',
  },
  AdditionalActivities: {
    current: ['AdditionalActivitiesRequested', 'AdditionalActivitiesReceived'],
    next: 'AdditionalActivitiesNotRequired',
    message: 'Do you really want to cancel the additional activities with {candidateName}?',
  },
};

export const getTaskStatusByDuration = (
  durationTime: number,
  deadlines?: { normal: number; warning: number },
): TaskStyles => {
  if (!deadlines) return TaskDeadlineStyle.Normal;

  const { normal, warning } = deadlines;

  if (durationTime >= warning) return TaskDeadlineStyle.Expired;
  if (durationTime >= normal) return TaskDeadlineStyle.Warning;

  return TaskDeadlineStyle.Normal;
};

export const getRecruitingSlaDuration = (startDate: MomentInput, endDate: MomentInput): number => {
  const start = moment(startDate).tz(RECRUITING_SLA_SCHEDULE.timezone);
  const end = moment(endDate).tz(RECRUITING_SLA_SCHEDULE.timezone);

  if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
    return 0;
  }

  let duration = 0;
  const currentDay = start.clone().startOf('day');

  while (currentDay.isBefore(end)) {
    const isWeekday = currentDay.isoWeekday() <= 5;

    if (isWeekday) {
      const workdayStart = currentDay.clone().hour(RECRUITING_SLA_SCHEDULE.workdayStartHour);
      const workdayEnd = currentDay.clone().hour(RECRUITING_SLA_SCHEDULE.workdayEndHour);
      const intervalStart = moment.max(start, workdayStart);
      const intervalEnd = moment.min(end, workdayEnd);

      if (intervalEnd.isAfter(intervalStart)) duration += intervalEnd.diff(intervalStart);
    }

    currentDay.add(1, 'day');
  }

  return duration;
};

export const formatTime = (milliseconds: number) => {
  const minutes = Math.floor(milliseconds / 60000);
  const hours = Math.floor(minutes / 60);
  const workingDayHours = RECRUITING_SLA_WORKING_DAY / 60 / 60 / 1000;
  const days = Math.floor(hours / workingDayHours);

  const remainingMinutes = minutes % 60;
  const remainingHours = hours % workingDayHours;

  return [
    days > 0 ? `${days}d` : '',
    remainingHours > 0 || days > 0 ? `${remainingHours}h` : '',
    `${remainingMinutes}m`,
  ]
    .filter(Boolean)
    .join(' ');
};

export const countItemsWithStatus = (data: any[], stage: string): number => {
  return data.reduce((total, item) => {
    const countInItem = item?.items?.filter((position: any) => position.stage === stage).length;
    return total + countInItem;
  }, 0);
};

export const getBorderStyles = (columnState: ColumnState) => {
  if (columnState === ColumnState.VALID_DROP) {
    return {
      borderStyle: 'dashed',
      borderWidth: 2,
      borderColor: 'primary.main',
    };
  } else if (columnState === ColumnState.INVALID_DROP) {
    return {
      borderStyle: 'dashed',
      borderWidth: 2,
      borderColor: 'error.main',
    };
  }

  return {
    borderStyle: 'solid',
    borderWidth: 2,
    borderColor: 'transparent',
  };
};

export const getShortName = (fullName: string) => {
  if (!fullName) {
    return '';
  }

  const parts = fullName.trim().split(' ');

  if (parts.length === 2) {
    const [firstName, lastName] = parts;
    return `${firstName.charAt(0)}.${lastName}`;
  }

  return fullName;
};

type CancellationConfirmationMessageParams = {
  jobName: string;
  currentKey: string;
  nextKey: string;
  candidateName: string;
};

export const getCancellationConfirmationMessage = ({
  jobName,
  currentKey,
  nextKey,
  candidateName,
}: CancellationConfirmationMessageParams) => {
  const confirmationConfig = CANCELLATION_CONFIRMATION_CONFIG[jobName];

  if (!confirmationConfig || confirmationConfig.next !== nextKey || !confirmationConfig.current.includes(currentKey))
    return null;

  return confirmationConfig.message.replace('{candidateName}', candidateName);
};
